import {
  Alert,
  AlertIcon,
  AlertTitle,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  ListItem,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Tooltip,
  UnorderedList,
  VStack,
  useRadioGroup,
} from '@chakra-ui/react';
import { InformationCircleIcon } from '@heroicons/react/outline';
import { UserGroupIcon, UserIcon } from '@heroicons/react/solid';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from 'use-intl';

import { CompanyCapitalType, FundraisingType } from '@blockpulse3/graphql/hooks';
import {
  ErrorMessage,
  ResponsiveModal,
  ResponsiveModalFooter,
  ResponsiveModalProps,
} from '@blockpulse3/ui/commons';

import { schemaFundraisingHeadlessCompanyInfo } from './schema';
import {
  IFundraisingHeadlessCompanyDefaultValues,
  IFundraisingHeadlessCompanyModal,
} from './types';

type Props = {
  onSubmit: (data: IFundraisingHeadlessCompanyModal) => void;
  isFeatureFundraisingHeadless?: boolean;
  defaultValues: IFundraisingHeadlessCompanyDefaultValues | undefined;
  companyName: string;
} & Omit<ResponsiveModalProps, 'children'>;

export function FundraisingHeadlessCompanyModal({
  onSubmit,
  isFeatureFundraisingHeadless,
  defaultValues,
  companyName,
  ...props
}: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nFundraisingHeadlessCompanyModal = useTranslations('FundraisingHeadlessCompanyModal');
  const i18nFundraisingPrivate = useTranslations('FundraisingTypeModal.FundraisingPrivate');
  const i18nFundraisingCrowdfunding = useTranslations(
    'FundraisingTypeModal.FundraisingCrowdfunding',
  );

  const capitalInputRef = useRef<HTMLDivElement>(null);

  const { register, handleSubmit, formState, setValue, getValues, clearErrors } =
    useForm<IFundraisingHeadlessCompanyModal>({
      resolver: yupResolver(schemaFundraisingHeadlessCompanyInfo),
      defaultValues,
    });

  const { getRadioProps } = useRadioGroup({
    name: 'capitalType',
    onChange: (value: CompanyCapitalType): void => {
      setValue('capitalType', value);
      setValue('minimumShareCapital', null);
      setValue('maximumShareCapital', null);
      clearErrors('minimumShareCapital');
      clearErrors('maximumShareCapital');
      clearErrors('capitalType');
    },
  });

  const { getRadioProps: getRadioPropsFundraisingType } = useRadioGroup({
    name: 'fundraisingType',
    onChange: (value: FundraisingType): void => {
      setValue('fundraisingType', value);
      clearErrors('fundraisingType');
    },
  });

  const handleFormSubmit: SubmitHandler<IFundraisingHeadlessCompanyModal> = (data) => {
    onSubmit(data);
  };

  useEffect(() => {
    if (capitalInputRef.current) {
      capitalInputRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  });

  return (
    <ResponsiveModal scrollBehavior="inside" {...props}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="lg">
          {i18nFundraisingHeadlessCompanyModal('Title', { companyName })}
        </ModalHeader>
        <ModalBody scrollBehavior="auto">
          <form id="headless-company" onSubmit={handleSubmit(handleFormSubmit)}>
            {isFeatureFundraisingHeadless && (
              <FormControl isInvalid={!!formState.errors?.fundraisingType} mb="24px">
                <FormLabel>{i18nFundraisingHeadlessCompanyModal('FundraisingTypeLabel')}</FormLabel>
                <RadioGroup
                  as={Stack}
                  direction={{ base: 'column', md: 'row' }}
                  id="fundraisingType"
                  spacing="4"
                >
                  <Flex borderWidth="1px" width={{ base: 'full', md: '50%' }}>
                    <Radio
                      alignItems="center"
                      p="3"
                      w="full"
                      {...getRadioPropsFundraisingType({ value: FundraisingType.PRIVATE })}
                    >
                      <HStack justifyContent="space-between">
                        <HStack spacing="2">
                          <Icon as={UserIcon} boxSize="24px" color="gray.700" />
                          <Text fontSize="sm" fontWeight="medium">
                            {i18nFundraisingPrivate('Title')}
                          </Text>
                        </HStack>
                        <Tooltip
                          hasArrow
                          p="8px"
                          placement="top"
                          label={
                            <UnorderedList fontWeight="normal" pl="2.5">
                              <ListItem>{i18nFundraisingPrivate('KnowSubscribers')}</ListItem>
                              <ListItem>
                                {i18nFundraisingPrivate('KnowTheirSubscriptionAmounts')}
                              </ListItem>
                              <ListItem>{i18nFundraisingPrivate('LessSubscribers')}</ListItem>
                            </UnorderedList>
                          }
                        >
                          <Icon as={InformationCircleIcon} boxSize="16px" color="gray.700" />
                        </Tooltip>
                      </HStack>
                    </Radio>
                  </Flex>
                  <Flex alignItems="center" borderWidth="1px" width={{ base: 'full', md: '50%' }}>
                    <Radio
                      p="3"
                      w="full"
                      {...getRadioPropsFundraisingType({ value: FundraisingType.CROWDFUNDING })}
                    >
                      <HStack justifyContent="space-between">
                        <HStack spacing="2">
                          <Icon as={UserGroupIcon} boxSize="24px" color="gray.700" />
                          <Text fontSize="sm" fontWeight="medium">
                            {i18nFundraisingCrowdfunding('Title')}
                          </Text>
                        </HStack>
                        <Tooltip
                          hasArrow
                          p="8px"
                          placement="top"
                          label={
                            <UnorderedList fontWeight="normal" pl="2.5">
                              <ListItem>{i18nFundraisingCrowdfunding('MoreSubscribers')}</ListItem>
                              <ListItem>
                                {i18nFundraisingCrowdfunding('UnknownAmountsAndSubscribers')}
                              </ListItem>
                            </UnorderedList>
                          }
                        >
                          <Icon as={InformationCircleIcon} boxSize="16px" color="gray.700" />
                        </Tooltip>
                      </HStack>
                    </Radio>
                  </Flex>
                </RadioGroup>
                <ErrorMessage error={formState.errors?.fundraisingType} />
              </FormControl>
            )}
            <Alert mb="16px" status="info">
              <AlertIcon />
              <Stack spacing="3">
                <AlertTitle>{i18nFundraisingHeadlessCompanyModal('AlertTitle')}</AlertTitle>
              </Stack>
            </Alert>
            <VStack gap="24px">
              <FormControl isInvalid={!!formState.errors?.nominalValue}>
                <FormLabel htmlFor="nominalValue">
                  {i18nFundraisingHeadlessCompanyModal('NominalValue')}
                </FormLabel>
                <InputGroup>
                  <Input
                    id="nominalValue"
                    step="0.01"
                    type="number"
                    {...register('nominalValue')}
                  />
                  <InputRightElement color="gray.500">€</InputRightElement>
                </InputGroup>
                <ErrorMessage error={formState.errors?.nominalValue} />
              </FormControl>
              <FormControl isInvalid={!!formState.errors?.shareCapital}>
                <FormLabel htmlFor="shareCapital">
                  {i18nFundraisingHeadlessCompanyModal('ShareCapital')}
                </FormLabel>
                <InputGroup>
                  <Input
                    id="shareCapital"
                    step="0.01"
                    type="number"
                    {...register('shareCapital')}
                  />
                  <InputRightElement color="gray.500">€</InputRightElement>
                </InputGroup>
                <ErrorMessage error={formState.errors.shareCapital} />
              </FormControl>
              <FormControl isInvalid={!!formState.errors?.capitalType}>
                <FormLabel>{i18nFundraisingHeadlessCompanyModal('CapitalType')}</FormLabel>
                <RadioGroup
                  as={Stack}
                  direction={{ base: 'column', md: 'row' }}
                  id="capitalType"
                  spacing="4"
                >
                  <Flex borderWidth="1px" width={{ base: 'full', md: '50%' }}>
                    <Radio p="3" w="full" {...getRadioProps({ value: CompanyCapitalType.FIXED })}>
                      {i18nFundraisingHeadlessCompanyModal('CapitalFixed')}
                    </Radio>
                  </Flex>
                  <Flex borderWidth="1px" width={{ base: 'full', md: '50%' }}>
                    <Radio
                      p="3"
                      w="full"
                      {...getRadioProps({ value: CompanyCapitalType.VARIABLE })}
                    >
                      {i18nFundraisingHeadlessCompanyModal('CapitalVariable')}
                    </Radio>
                  </Flex>
                </RadioGroup>
                <ErrorMessage error={formState.errors?.capitalType} />
              </FormControl>
              {getValues('capitalType') === CompanyCapitalType.VARIABLE && (
                <>
                  <FormControl
                    isInvalid={!!formState.errors?.minimumShareCapital}
                    ref={capitalInputRef}
                  >
                    <FormLabel htmlFor="minimumShareCapital">
                      {i18nFundraisingHeadlessCompanyModal('CapitalMin')}
                    </FormLabel>
                    <InputGroup>
                      <Input
                        id="minimumShareCapital"
                        step="0.01"
                        type="number"
                        {...register('minimumShareCapital')}
                      />
                      <InputRightElement color="gray.500">€</InputRightElement>
                    </InputGroup>
                    <ErrorMessage error={formState.errors.minimumShareCapital} />
                  </FormControl>
                  <FormControl isInvalid={!!formState.errors?.maximumShareCapital}>
                    <FormLabel htmlFor="maximumShareCapital">
                      {i18nFundraisingHeadlessCompanyModal('CapitalMax')}
                    </FormLabel>
                    <InputGroup>
                      <Input
                        id="maximumShareCapital"
                        step="0.01"
                        type="number"
                        {...register('maximumShareCapital')}
                      />
                      <InputRightElement color="gray.500">€</InputRightElement>
                    </InputGroup>
                    <ErrorMessage error={formState.errors.maximumShareCapital} />
                  </FormControl>
                </>
              )}
            </VStack>
          </form>
        </ModalBody>
        <Divider />
        <ResponsiveModalFooter>
          <Button variant="secondary" onClick={props.onClose}>
            {i18nGeneral('Cancel')}
          </Button>
          <Button form="headless-company" type="submit">
            {i18nGeneral('Submit')}
          </Button>
        </ResponsiveModalFooter>
      </ModalContent>
    </ResponsiveModal>
  );
}
